import {create} from "zustand";
import {useMiniGames} from "./useMiniGames";

/**
 * Hook specifically for home page components that need access to complete games list
 * for metadata lookup (icons, names, etc.) without being affected by filters.
 *
 * PERF: This store no longer makes its own HTTP call. It reuses the games list
 * already fetched by useMiniGames, eliminating a duplicate GAMES_LIST + favorites
 * request on every Home page load (~2 HTTP requests saved).
 */
export const useGameMetadataStore = create((set, get) => ({
  // Complete unfiltered games list for metadata lookup
  allGames: [],
  loading: false,
  error: null,
  initialized: false,

  /**
   * Initialize from useMiniGames store data.
   * If useMiniGames already has data, copies it immediately (no HTTP call).
   * If not, marks as loading — the subscription below will auto-populate
   * when useMiniGames finishes its own fetch.
   */
  initializeGames: () => {
    const {initialized, loading} = get();

    // Prevent multiple simultaneous calls
    if (initialized || loading) return;

    // Try to populate from useMiniGames if it already has data
    const miniGamesList = useMiniGames.getState().list;
    if (miniGamesList.length > 0) {
      set({
        allGames: miniGamesList,
        loading: false,
        initialized: true,
        error: null,
      });
      return;
    }

    // useMiniGames hasn't loaded yet — mark as loading.
    // The subscription below will auto-populate when data arrives.
    set({loading: true, error: null});
  },

  getGameById: (gameId, provider = null) => {
    const {allGames} = get();

    if (provider) {
      return allGames.find(game => (game.id === gameId || game.id === String(gameId)) && game.provider === provider);
    }

    return allGames.find(game => game.id === gameId || game.id === String(gameId));
  },

  // Get game by name - normalized lookup for matching with external data
  getGameByName: gameName => {
    const {allGames} = get();

    const normalize = (str = "") =>
      str
        .toLowerCase()
        .trim()
        .replace(/[''`´]/g, "")
        .replace(/[^a-z0-9áéíóúüñ\s]/gi, "")
        .replace(/\s+/g, " ");

    const normalizedSearchName = normalize(gameName);

    return allGames.find(game => normalize(game.name) === normalizedSearchName);
  },

  // Create a Map for efficient lookups by name (useful for bulk operations)
  getGamesMap: () => {
    const {allGames} = get();

    const normalize = (str = "") =>
      str
        .toLowerCase()
        .trim()
        .replace(/[''`´]/g, "")
        .replace(/[^a-z0-9áéíóúüñ\s]/gi, "")
        .replace(/\s+/g, " ");

    return new Map(allGames.map(game => [normalize(game.name), game]));
  },

  // Reset the store (useful for auth state changes)
  reset: () => {
    set({
      allGames: [],
      loading: false,
      error: null,
      initialized: false,
    });
  },
}));

/**
 * PERF: Cross-store subscription.
 * When useMiniGames finishes fetching its games list, auto-populate
 * useGameMetadataStore so components that depend on it get data
 * without an extra HTTP call.
 */
useMiniGames.subscribe(state => {
  const list = state.list;
  if (list.length === 0) return;

  const {initialized} = useGameMetadataStore.getState();
  if (initialized) return;

  useGameMetadataStore.setState({
    allGames: list,
    loading: false,
    initialized: true,
    error: null,
  });
});
